<template>
  <div>
    <div
      class="fixed left-0 top-0 z-[1502] flex border-0 border-y border-solid border-gray-500 bg-gray-900 dark:border-gray-500 dark:bg-gray-900"
      :class="{ '!top-[32px]': topBarStore.showWindowBar }"
      :style="alignBarLeft"
    >
      <div class="w-full">
        <div v-if="showMainTopBar" class="flex items-center px-5 py-2">
          <div class="flex flex-1 py-1">
            <t-button
              v-if="!isPreview && checkVisibility('undoRedo')"
              data-testid="undo"
              class="!h-8 w-8"
              :disabled="snapshotsLength === 0"
              @click="goBack"
            >
              <template #icon>
                <Undo />
              </template>
            </t-button>
            <t-button
              v-if="!isPreview && checkVisibility('undoRedo')"
              :disabled="redoSnapshotsLength <= 0"
              data-testid="redo"
              class="ml-2 !h-8 w-8"
              @click="goForward"
            >
              <template #icon>
                <Redo />
              </template>
            </t-button>
            <t-button
              v-if="!isPreview && checkVisibility('changePreview')"
              data-testid="preview-and-testing"
              class="ml-4"
              :loading="previewLoading || generalStore.loadingTemplate"
              @click="togglePreviewMode"
              >{{ $t("layout.preview_and_testing") }}</t-button
            >

            <t-button
              v-if="isPreview && checkVisibility('changePreview')"
              data-testid="back-to-editor"
              @click="togglePreviewMode"
            >
              {{ $t("layout.close_preview") }}</t-button
            >
            <div
              v-if="
                checkVisibility('changeLanguage') && generalStore.multilingual
              "
              class="ml-2.5"
            >
              <LanguageSettings />
            </div>
            <div
              v-if="
                checkVisibility('translate') &&
                !generalStore.multilingual &&
                openAiStore.openAiEnabled
              "
              class="ml-2.5"
            >
              <TranslateSettings />
            </div>
          </div>

          <div class="flex flex-1 justify-center">
            <div style="display: flex">
              <TabGroup
                v-if="checkVisibility('previewSize') && isPreview"
                :default-index="mobile ? 0 : 1"
                @change="onInPreviewTabChanged"
              >
                <TabList class="flex space-x-1 rounded-xl bg-gray-800 p-1">
                  <Tab
                    v-slot="{ selected }"
                    as="div"
                    class="flex w-full focus:outline-none"
                  >
                    <button
                      tooltip="mobile"
                      data-testid="preview-mobile"
                      :class="[
                        'grid h-8 w-8 items-center justify-center rounded-md font-inter text-xs font-medium leading-5 text-gray-100 transition duration-300',
                        selected
                          ? 'bg-gray-600 text-gray-50 outline outline-2 outline-offset-[-2px] outline-accent-1'
                          : 'cursor-pointer bg-gray-800 text-gray-100 hover:bg-gray-600 hover:text-gray-50',
                      ]"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.3125 13.6875H8.6875C8.44141 13.6875 8.25 13.9062 8.25 14.125C8.25 14.3711 8.44141 14.5625 8.6875 14.5625H11.3125C11.5312 14.5625 11.75 14.3711 11.75 14.125C11.75 13.9062 11.5312 13.6875 11.3125 13.6875ZM13.0625 2.75H6.9375C5.95312 2.75 5.1875 3.54297 5.1875 4.5V15C5.1875 15.9844 5.95312 16.75 6.9375 16.75H13.0625C14.0195 16.75 14.8125 15.9844 14.8125 15V4.5C14.8125 3.54297 14.0195 2.75 13.0625 2.75ZM13.5 15C13.5 15.2461 13.2812 15.4375 13.0625 15.4375H6.9375C6.69141 15.4375 6.5 15.2461 6.5 15V4.5C6.5 4.28125 6.69141 4.0625 6.9375 4.0625H8.25V4.9375C8.25 5.18359 8.44141 5.375 8.6875 5.375H11.3125C11.5312 5.375 11.75 5.18359 11.75 4.9375V4.0625H13.0625C13.2812 4.0625 13.5 4.28125 13.5 4.5V15Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  </Tab>
                  <Tab
                    v-slot="{ selected }"
                    as="div"
                    class="flex w-full focus:outline-none"
                  >
                    <button
                      tooltip="laptop"
                      data-testid="preview-desktop"
                      :class="[
                        'grid h-8 w-8 items-center justify-center rounded-md font-inter text-xs font-medium leading-5 text-gray-100 transition duration-300',
                        selected
                          ? 'bg-gray-600 text-gray-50 outline outline-2 outline-offset-[-2px] outline-accent-1'
                          : 'cursor-pointer bg-gray-800 text-gray-100 hover:bg-gray-600 hover:text-gray-50',
                      ]"
                    >
                      <font-awesome-icon :icon="['fas', 'laptop']" />
                    </button>
                  </Tab>
                </TabList>
              </TabGroup>
              <TabGroup
                v-if="
                  checkVisibility('previewSize') &&
                  !isPreview &&
                  mjmlStore.isMobileFirstEnabled
                "
                :default-index="mjmlStore.isMobileView ? 0 : 1"
                @change="onPreviewTabChanged"
              >
                <TabList class="flex space-x-1 rounded-xl bg-gray-800 p-1">
                  <Tab
                    v-slot="{ selected }"
                    as="div"
                    class="flex w-full focus:outline-none"
                  >
                    <button
                      tooltip="mobile"
                      data-testid="mobile-first-mobile-button"
                      :class="[
                        'grid h-8 w-8 items-center justify-center rounded-md font-inter text-xs font-medium leading-5 text-gray-100 transition duration-300',
                        selected
                          ? 'bg-gray-600 text-gray-50 outline outline-2 outline-offset-[-2px] outline-accent-1'
                          : 'cursor-pointer bg-gray-800 text-gray-100 hover:bg-gray-600 hover:text-gray-50',
                      ]"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.3125 13.6875H8.6875C8.44141 13.6875 8.25 13.9062 8.25 14.125C8.25 14.3711 8.44141 14.5625 8.6875 14.5625H11.3125C11.5312 14.5625 11.75 14.3711 11.75 14.125C11.75 13.9062 11.5312 13.6875 11.3125 13.6875ZM13.0625 2.75H6.9375C5.95312 2.75 5.1875 3.54297 5.1875 4.5V15C5.1875 15.9844 5.95312 16.75 6.9375 16.75H13.0625C14.0195 16.75 14.8125 15.9844 14.8125 15V4.5C14.8125 3.54297 14.0195 2.75 13.0625 2.75ZM13.5 15C13.5 15.2461 13.2812 15.4375 13.0625 15.4375H6.9375C6.69141 15.4375 6.5 15.2461 6.5 15V4.5C6.5 4.28125 6.69141 4.0625 6.9375 4.0625H8.25V4.9375C8.25 5.18359 8.44141 5.375 8.6875 5.375H11.3125C11.5312 5.375 11.75 5.18359 11.75 4.9375V4.0625H13.0625C13.2812 4.0625 13.5 4.28125 13.5 4.5V15Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  </Tab>
                  <Tab
                    v-slot="{ selected }"
                    as="div"
                    class="flex w-full focus:outline-none"
                  >
                    <button
                      tooltip="laptop"
                      data-testid="mobile-first-desktop-button"
                      :class="[
                        'grid h-8 w-8 items-center justify-center rounded-md font-inter text-xs font-medium leading-5 text-gray-100 transition duration-300',
                        selected
                          ? 'bg-gray-600 text-gray-50 outline outline-2 outline-offset-[-2px] outline-accent-1'
                          : 'cursor-pointer bg-gray-800 text-gray-100 hover:bg-gray-600 hover:text-gray-50',
                      ]"
                    >
                      <font-awesome-icon :icon="['fas', 'laptop']" />
                    </button>
                  </Tab>
                </TabList>
              </TabGroup>
              <div
                v-if="!isPreview && mjmlStore.isMobileFirstEnabled"
                style="display: flex; align-items: center"
              >
                <t-switch
                  v-model="blurHiddenElements"
                  data-testid="toggle-hidden-elements-view"
                  style="margin-left: 10px"
                  :label="$t('layout.show_hidden')"
                ></t-switch>
              </div>
            </div>
          </div>
          <div class="flex flex-1 justify-end gap-[10px]">
            <t-button
              v-if="chatAIStore.chatAiIsEnable && !isPreview"
              v-tooltip="{ content: $t('layout.chatAI.tooltip'), delay: 350 }"
              data-testid="chatAI"
              class="!h-8 w-8"
              icon
              @click="openChatAI"
            >
              <template #icon>
                <AIIcon />
              </template>
            </t-button>
            <t-button
              v-if="commentsStore.enableComments && !isPreview"
              v-tooltip="{ content: $t('layout.comments.tooltip'), delay: 350 }"
              data-testid="comments-topbar"
              class="!h-8 w-8"
              icon
              @click="openComments"
            >
              <template #icon>
                <Comments />
                <span
                  v-if="unreadComments"
                  data-testid="unread"
                  class="absolute right-1 top-1 flex h-[10px] w-[10px]"
                >
                  <span
                    class="absolute inline-flex h-full w-full animate-ping rounded-full bg-radial opacity-75"
                  ></span>
                  <span
                    class="relative box-border inline-flex h-[10px] w-[10px] rounded-full border border-solid border-white/[.18] bg-radial"
                  ></span>
                </span>
              </template>
            </t-button>
            <t-button
              v-if="autosavesStore.autosavesEnabled && !isPreview"
              v-tooltip="{ content: $t('layout.autosaves'), delay: 350 }"
              data-testid="autosaves"
              class="!h-8 w-8"
              icon
              @click="openAutoSaveMenu"
            >
              <template #icon>
                <font-awesome-icon :icon="['fas', 'clock-rotate-left']" />
              </template>
            </t-button>
            <t-button
              v-if="checkVisibility('save') && !checkVisibility('saveAndClose')"
              data-testid="save"
              :loading="generalStore.saving"
              @click="save()"
              >{{ $t("layout.save") }}</t-button
            >
            <t-button
              v-if="checkVisibility('saveAndClose') && !checkVisibility('save')"
              success
              data-testid="save-and-close"
              :loading="generalStore.saving || generalStore.savingAndClosing"
              @click="saveAndClose"
              >{{ $t("layout.save_and_close") }}</t-button
            >
            <TButtonDropdown
              v-if="checkVisibility('save') && checkVisibility('saveAndClose')"
              success
              data-testid="save"
              :loading="generalStore.saving || generalStore.savingAndClosing"
              @click="save()"
            >
              {{ $t("layout.save") }}

              <template #dropdown>
                <div
                  class="list-item"
                  data-testid="save-and-close"
                  @click="saveAndClose"
                >
                  {{ $t("layout.save_and_close") }}
                </div>
              </template>
            </TButtonDropdown>
          </div>
        </div>
        <div
          v-if="showPreviewTopBar && isPreview"
          class="grid grid-cols-3 items-center bg-gray-800 px-5 py-2"
        >
          <div class="flex items-center gap-6">
            <TButton
              v-if="mergeTagsStore.enableMergeTagPreview"
              data-testid="mergetags-panel-open"
              @click="
                mergeTagsStore.showMergePreviewPanel =
                  !mergeTagsStore.showMergePreviewPanel
              "
            >
              <template #icon>
                <MergeTag />
              </template>
              {{ $t("layout.merge_tags_preview.show_panel") }}
            </TButton>
            <TemplateSizingBoxes
              v-if="enableImageToTextRatio"
              :label="$t('layout.image_ratio.title')"
              :skore="imageRatioString"
              :good="imageRatio < 2 / 3"
              :middle="imageRatio >= 2 / 3 && imageRatio <= 1"
              :bad="imageRatio > 1"
              :modal-explain-text="$t('layout.image_ratio.explain_text')"
              :modal-status-tetx="
                imageRatio < 2 / 3
                  ? $t('layout.image_ratio.good')
                  : imageRatio > 1
                    ? $t('layout.image_ratio.bad')
                    : $t('layout.image_ratio.middle')
              "
              :loading="isRatioLoading"
            />
          </div>
          <div class="justify-self-center">
            <div
              v-if="previewStore.enableDarkMode"
              class="flex items-center gap-3"
            >
              <TSwitch
                v-model="darkMode"
                :label="$t('layout.dark_mode')"
              ></TSwitch>
              <div
                v-tooltip="{
                  content: `<div style='max-width: 300px; text-align: center; line-height: 1.3'>${$t('layout.dark_mode_warning')}</div>`,
                  html: true,
                  placement: 'bottom',
                }"
                class="h-[18px] w-[18px] text-meh"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="17" r="1" fill="currentColor" />
                  <path
                    d="M12 10L12 14"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M3.44722 18.1056L10.2111 4.57771C10.9482 3.10361 13.0518 3.10362 13.7889 4.57771L20.5528 18.1056C21.2177 19.4354 20.2507 21 18.7639 21H5.23607C3.7493 21 2.78231 19.4354 3.44722 18.1056Z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div id="sendPreview" class="justify-self-end">
            <div
              v-if="checkVisibility('previewTestMail')"
              class="flex items-center"
            >
              <t-note style="margin-right: 12px; font-weight: 500">{{
                $t("layout.send_test")
              }}</t-note>
              <TInputWithSelect
                v-if="testingEmailAddresses !== undefined"
                v-model="recipientEmails"
                data-testid="test-email-input"
                type="email"
                :options="testingEmailAddresses"
                style="width: 256px; margin-right: 5px"
                :placeholder="$t('layout.email_address')"
                :options-placeholder="$t('layout.email_address')"
                :error-text="$t('layout.invalid_email')"
                @enter-pressed="sendPreview"
                @update-options="updateTestingEmailAddresses"
              />
              <t-input
                v-else
                v-model="recipientEmail"
                data-testid="test-email-input"
                style="width: 200px; margin-right: 5px"
                name="email"
                :placeholder="$t('layout.email_address')"
                autocomplete="email"
                @enter-pressed="sendPreview"
              />
              <t-button
                data-testid="send-test-email"
                :disabled="!isEmailValid || generalStore.sentLimitExceeded"
                :loading="generalStore.sendingTestEmail"
                @click="sendPreview"
              >
                {{ $t("layout.send") }}
                <template #iconRight>
                  <Send />
                </template>
              </t-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import TemplateSizingBoxes from "./_internal/TemplateSizingBoxes.vue";
  import TInputWithSelect from "./_internal/TInputWithSelect.vue";
  import LanguageSettings from "./LanguageSettings/LanguageSettings.vue";
  import TranslateSettings from "./TranslateSettings/TranslateSettings.vue";

  import { useMergeTagsStore } from "@/stores/mergeTags";
  import { useTopBarStore } from "@/stores/topBar";
  import { usePreviewStore } from "@/stores/preview";
  import { useAutosavesStore } from "@/stores/autosaves";
  import { useLayoutStore } from "@/stores/layoutStore";
  import { useGeneralStore } from "@/stores/generalStore";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useChatAI } from "@/stores/chatAIStore";
  import { useTemplateSizingStore } from "@/stores/templateSizingStore";
  import { useCommentsStore } from "@/stores/commentsStore";
  import { useOpenAiStore } from "@/stores/openAiStore";

  import validateEmail from "@/utils/validateEmail";
  import useActions from "@/actions/useActions";

  import { Send, Undo, Redo, AIIcon, Comments, MergeTag } from "@topol/icons";
  import {
    TButton,
    TInput,
    TNote,
    TSwitch,
    TButtonDropdown,
  } from "@topol/twig-ui";

  import { TabGroup, TabList, Tab } from "@headlessui/vue";
  import { watch, ref, computed } from "vue";

  const props = defineProps({
    isMini: {
      type: Boolean,
      default: false,
    },
  });

  const mergeTagsStore = useMergeTagsStore();
  const topBarStore = useTopBarStore();
  const previewStore = usePreviewStore();
  const autosavesStore = useAutosavesStore();
  const layoutStore = useLayoutStore();
  const generalStore = useGeneralStore();
  const mjmlStore = useMjmlStore();
  const chatAIStore = useChatAI();
  const templateSizingStore = useTemplateSizingStore();
  const commentsStore = useCommentsStore();
  const openAiStore = useOpenAiStore();
  const { save, saveAndClose, sendTestEmail } = useActions();

  const mobile = ref(false);
  const recipientEmail = ref("");
  const previewLoading = ref(false);

  const testingEmailAddresses = computed(() => {
    return previewStore.testingEmailAddresses;
  });

  const darkMode = computed({
    get() {
      return previewStore.darkMode;
    },
    set(value: boolean) {
      previewStore.darkMode = value;
    },
  });

  const recipientEmails = ref<string[]>(
    testingEmailAddresses.value && testingEmailAddresses.value[0]
      ? [testingEmailAddresses.value[0]]
      : []
  );

  watch(mjmlStore.snapshots, () => {
    const message = {
      type: "callback",
      action: "onUndoChange",
      data: {
        args: [mjmlStore.snapshots.length],
      },
    };
    window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");
  });

  watch(mjmlStore.redoSnapshots, () => {
    const message = {
      type: "callback",
      action: "onRedoChange",
      data: {
        args: [mjmlStore.redoSnapshots.length],
      },
    };
    window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");
  });

  watch(mobile, (isMobile) => {
    if (isMobile) {
      previewStore.setPreviewSize("375px");
    } else {
      previewStore.setPreviewSize("100%");
    }
  });

  const isEmailValid = computed(() => {
    if (testingEmailAddresses.value !== undefined) {
      let isValid = true;
      recipientEmails.value.map((email) => {
        if (!validateEmail(email)) {
          isValid = false;
        }
      });
      return isValid && recipientEmails.value.length > 0;
    }
    return validateEmail(recipientEmail.value);
  });

  const isPreview = computed(() => {
    return previewStore.previewMode;
  });

  const snapshotsLength = computed(() => {
    return mjmlStore.snapshots.length;
  });

  const redoSnapshotsLength = computed(() => {
    return mjmlStore.redoSnapshots.length;
  });

  const alignRight = computed(() => {
    return layoutStore.mainMenuAlign === "right";
  });

  const alignBarLeft = computed(() => {
    if (isPreview.value || layoutStore.hideControlPanel === true) {
      return {
        marginLeft: "0px",
        width: "100%",
      };
    } else {
      let padding = "310px";

      if (props.isMini) {
        padding = "352px";
      }

      return {
        marginLeft: alignRight.value ? "0px" : padding,
        width: `calc(100% - ${padding})`,
      };
    }
  });

  const visibleTopBarElements = computed(() => {
    return topBarStore.visibleTopBarElements;
  });

  const imageRatio = computed(() => {
    if (Math.round(templateSizingStore.nonImageContent) !== 0) {
      return (
        Math.round(templateSizingStore.imageContent) /
        Math.round(templateSizingStore.nonImageContent)
      );
    }

    return 100;
  });

  const isRatioLoading = computed(() => {
    return previewStore.isRatioLoading;
  });

  const unreadComments = computed(() => {
    return commentsStore.unread;
  });

  const enableImageToTextRatio = computed(() => {
    return previewStore.enableImageToTextRatio;
  });

  const imageRatioString = computed(() => {
    const wholeContent =
      templateSizingStore.imageContent + templateSizingStore.nonImageContent;

    const ratio = `${Math.round(
      (templateSizingStore.imageContent / wholeContent) * 100
    )}:${Math.round(
      (templateSizingStore.nonImageContent / wholeContent) * 100
    )}`;

    return ratio;
  });

  const showMainTopBar = computed(() => {
    return topBarStore.showMainTopBar;
  });

  const showPreviewTopBar = computed(() => {
    return topBarStore.showPreviewTopBar;
  });

  function toggleHiddenElemenetsBlur() {
    mjmlStore.blurHiddenElements = !mjmlStore.blurHiddenElements;
  }

  const blurHiddenElements = computed({
    get() {
      return !mjmlStore.blurHiddenElements;
    },
    set() {
      toggleHiddenElemenetsBlur();
    },
  });

  function onInPreviewTabChanged(index: number) {
    if (index === 0) {
      mobile.value = true;
    } else {
      mobile.value = false;
    }
  }

  function onPreviewTabChanged(index: number) {
    if (index === 0) {
      changeToMobileView();
    } else {
      changeToDesktopView();
    }
  }

  function changeToMobileView() {
    return mjmlStore.setIsMobileView(true);
  }

  function changeToDesktopView() {
    return mjmlStore.setIsMobileView(false);
  }

  function openChatAI() {
    chatAIStore.toggleOpenChat();
  }

  function openComments() {
    commentsStore.toggleOpenComments();
  }

  function openAutoSaveMenu() {
    autosavesStore.toggleAutosaveMenu();
  }

  function checkVisibility(element: string) {
    if (visibleTopBarElements.value.length < 1) {
      return true;
    }

    return visibleTopBarElements.value.includes(element);
  }

  function togglePreviewMode() {
    previewStore.tooglePreviewMode();
  }

  function goBack() {
    mjmlStore.goBack();
  }

  function goForward() {
    mjmlStore.goForward();
  }

  function sendPreview() {
    if (!isEmailValid.value) return;
    sendTestEmail(
      testingEmailAddresses.value !== undefined
        ? recipientEmails.value
        : recipientEmail.value
    );
  }

  function updateTestingEmailAddresses(emails: string[]) {
    previewStore.testingEmailAddresses = emails;

    if (previewStore.testingAddressesLocalStorage) {
      localStorage.setItem("testingEmailAddresses", JSON.stringify(emails));
      return;
    }

    const message = {
      type: "callback",
      action: "updateTestingEmailAddresses",
      data: {
        args: [emails],
      },
    };

    window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");
  }
</script>
